/**
 * Vague 19 — Bouton de scan massif Friction Gap
 *
 * Modal de configuration → POST /api/workspace/origination/friction-gap/batch
 * Le tray LLMJobs prend ensuite la suite (polling + notif au done).
 *
 * Sélection : par étape(s) + secteur(s) + toggle with_actifs + cap max_jobs.
 * Compteur live des opps qui matchent.
 */
import { useMemo, useState } from "react";
import { Sparkles, X as XIcon, Loader2, AlertTriangle, CheckCircle, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";

const STEP_LABELS = {
  identification: "Identification",
  qualification: "Qualification",
  structuration: "Structuration",
  credibilisation: "Crédibilisation",
  financement: "Financement",
  participation: "Participation",
  controle: "Contrôle",
};

export default function BatchFrictionGapButton({ steps = [], secteurs = [], data }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-xs px-3 py-1.5 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 inline-flex items-center gap-1.5"
        data-testid="orig-batch-friction-btn"
        title="Lance une analyse Friction Gap en parallèle sur plusieurs opportunités"
      >
        <Zap className="w-3.5 h-3.5" />
        Scan massif
      </button>
      {open && (
        <BatchModal steps={steps} secteurs={secteurs} data={data} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function BatchModal({ steps, secteurs, data, onClose }) {
  const [selectedSteps, setSelectedSteps] = useState(["identification"]);
  const [selectedSecteurs, setSelectedSecteurs] = useState([]);
  const [withActifs, setWithActifs] = useState(false);
  const [maxJobs, setMaxJobs] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Compteur live : utilise le pipeline déjà chargé (data.by_step) pour estimer
  const matchedCount = useMemo(() => {
    if (!data?.by_step) return null;
    let count = 0;
    for (const stepKey of selectedSteps) {
      const opps = data.by_step[stepKey] || [];
      for (const o of opps) {
        if (selectedSecteurs.length === 0 || selectedSecteurs.includes(o.secteur)) {
          count += 1;
        }
      }
    }
    return count;
  }, [selectedSteps, selectedSecteurs, data]);

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const submit = async () => {
    if (selectedSteps.length === 0) {
      notify.error("Sélectionnez au moins une étape.");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const r = await api.post("/workspace/origination/friction-gap/batch", {
        etapes: selectedSteps,
        secteurs: selectedSecteurs.length > 0 ? selectedSecteurs : null,
        with_actifs: withActifs,
        max_jobs: Number(maxJobs) || 5,
      });
      setResult(r.data);
      notify.success(`${r.data.queued} scan(s) lancé(s) en arrière-plan. Suivi dans le tray.`);
    } catch (e) {
      notify.error(fmtError(e, "Lancement batch impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="orig-batch-modal">
      <div className="noam-card max-w-xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#7F77DD]" />
            <h3 className="text-base font-semibold text-zinc-100">Scan massif · Friction Gap</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="orig-batch-close">
            <XIcon className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        <p className="text-[11px] text-zinc-400 mb-4">
          Lance une analyse NOAM en parallèle sur plusieurs opportunités. Les résultats apparaîtront
          dans le tray en bas-droite. Maximum 5 jobs concurrents par organisation.
        </p>

        {!result && (
          <div className="space-y-4" data-testid="orig-batch-form">
            {/* Étapes */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Étape(s) du pipeline</div>
              <div className="flex flex-wrap gap-1.5">
                {steps.map((s) => {
                  const on = selectedSteps.includes(s);
                  const stepCount = data?.by_step?.[s]?.length || 0;
                  return (
                    <button
                      key={s}
                      onClick={() => toggle(selectedSteps, setSelectedSteps, s)}
                      className={`text-[11px] px-2.5 py-1 rounded border transition ${
                        on ? "border-[#7F77DD]/60 bg-[#7F77DD]/15 text-[#7F77DD]" : "border-white/10 text-zinc-400 hover:text-zinc-200"
                      }`}
                      data-testid={`orig-batch-step-${s}`}
                    >
                      {STEP_LABELS[s] || s} <span className="text-zinc-500">({stepCount})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Secteurs (optionnel) */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                Secteurs (optionnel) <span className="text-zinc-600 normal-case tracking-normal">— vide = tous</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {secteurs.map((s) => {
                  const on = selectedSecteurs.includes(s);
                  return (
                    <button
                      key={s}
                      onClick={() => toggle(selectedSecteurs, setSelectedSecteurs, s)}
                      className={`text-[11px] px-2.5 py-1 rounded border transition ${
                        on ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400 hover:text-zinc-200"
                      }`}
                      data-testid={`orig-batch-sector-${s}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={withActifs}
                  onChange={(e) => setWithActifs(e.target.checked)}
                  className="accent-[#7F77DD]"
                  data-testid="orig-batch-actifs"
                />
                Avec actifs
              </label>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Max jobs (cap 10)</div>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={maxJobs}
                  onChange={(e) => setMaxJobs(e.target.value)}
                  className="noam-input text-xs w-full"
                  data-testid="orig-batch-maxjobs"
                />
              </div>
            </div>

            {/* Compteur live */}
            <div className="bg-white/[0.02] border border-white/5 rounded p-3 text-center" data-testid="orig-batch-counter">
              {matchedCount === null ? (
                <span className="text-zinc-500 text-xs">Chargement…</span>
              ) : matchedCount === 0 ? (
                <span className="text-zinc-500 text-xs">Aucune opportunité ne matche.</span>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Opportunités cible</div>
                  <div className="text-2xl font-bold text-[#7F77DD] mt-1">{matchedCount}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {matchedCount > maxJobs ? `→ ${maxJobs} seront scannées (cap), ${matchedCount - maxJobs} ignorées` : "→ toutes seront scannées"}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 border border-white/10 text-zinc-400 hover:bg-white/5 rounded"
                data-testid="orig-batch-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={submitting || matchedCount === 0 || selectedSteps.length === 0}
                className="flex-1 text-xs px-3 py-1.5 bg-[#7F77DD] hover:bg-[#7F77DD]/90 text-white rounded disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
                data-testid="orig-batch-submit"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Lancer {matchedCount > 0 && matchedCount <= maxJobs ? matchedCount : Math.min(matchedCount || 0, maxJobs)} scan(s)
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="orig-batch-result">
            <div className="border border-[#1D9E75]/30 bg-[#1D9E75]/10 rounded p-3">
              <div className="flex items-center gap-2 text-[#1D9E75]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Batch lancé</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">Total matchés</div>
                  <div className="text-base font-bold text-zinc-200 mt-0.5">{result.total_matched}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">Lancés</div>
                  <div className="text-base font-bold text-[#1D9E75] mt-0.5">{result.queued}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">Ignorés</div>
                  <div className="text-base font-bold text-zinc-500 mt-0.5">{result.skipped?.length || 0}</div>
                </div>
              </div>
            </div>

            {result.skipped && result.skipped.length > 0 && (
              <div className="text-[11px] text-zinc-500">
                <div className="flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3 h-3 text-[#EF9F27]" />
                  <span>Ignorés :</span>
                </div>
                <ul className="space-y-0.5 ml-4">
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i} className="truncate">
                      • {s.titre} <span className="text-zinc-600">({s.reason})</span>
                    </li>
                  ))}
                  {result.skipped.length > 5 && <li className="text-zinc-600">… et {result.skipped.length - 5} de plus</li>}
                </ul>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full text-xs px-3 py-1.5 bg-[#7F77DD] text-white rounded inline-flex items-center justify-center gap-1.5"
              data-testid="orig-batch-result-close"
            >
              Voir le tray en bas-droite
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
